import { useState, useEffect, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Film, Plus, Zap, Clock, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { VideoEditorPage } from '@/components/VideoEditor';
import { reelsApi, driveApi } from '@/services/api';

const fmt = (s) => s >= 60
  ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
  : `0:${String(Math.floor(s)).padStart(2, '0')}`;

// ─── Project card ────────────────────────────────────────────────────────────
function ProjectCard({ project, onOpen, onDelete }) {
  const score = project.viral_score || 0;
  const scoreColor = score >= 85 ? '#00d4aa' : score >= 70 ? '#7c5cfc' : '#f59e0b';

  return (
    <div data-testid={`project-card-${project.id}`}
      className="group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 hover:scale-[1.02]"
      style={{ aspectRatio: '9/16', background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}
      onClick={() => onOpen(project)}>
      {/* Thumbnail */}
      {project.thumbnail_url
        ? <img src={project.thumbnail_url} alt={project.name} className="w-full h-full object-cover" />
        : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2"
            style={{ background: 'hsl(var(--surface-elevated))' }}>
            <Film size={32} style={{ color: 'hsl(var(--foreground-subtle))' }} />
          </div>
        )}

      {/* Viral score */}
      {score > 0 && (
        <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-bold"
          style={{ background: 'rgba(0,0,0,0.75)', color: scoreColor, backdropFilter: 'blur(4px)' }}>
          <Zap size={10} fill={scoreColor} />{score}
        </div>
      )}

      {/* Status badge */}
      {project.status === 'processing' && (
        <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs"
          style={{ background: 'rgba(0,0,0,0.75)', color: '#f59e0b' }}>
          <RefreshCw size={10} className="animate-spin" /> Procesando
        </div>
      )}

      {/* Duration */}
      <div className="absolute bottom-0 left-0 right-0 p-2"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)' }}>
        <p className="text-xs font-semibold text-white truncate">{project.name}</p>
        {project.duration_seconds > 0 && (
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Clock size={9} className="inline mr-1" />{fmt(project.duration_seconds)}
          </p>
        )}
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2"
        style={{ background: 'rgba(0,0,0,0.55)' }}>
        <button data-testid="edit-project-btn" onClick={e => { e.stopPropagation(); onOpen(project); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold"
          style={{ background: 'hsl(var(--primary))', color: '#000' }}>
          <Edit2 size={12} /> Editar
        </button>
        <button onClick={e => { e.stopPropagation(); onDelete(project.id); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs"
          style={{ background: 'hsl(var(--destructive) / 0.85)', color: '#fff' }}>
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function ReelsCreatorPage() {
  const { activeBrand } = useApp();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openProject, setOpenProject] = useState(null); // null | 'new' | project

  const loadProjects = useCallback(async () => {
    if (!activeBrand?.id) return;
    setLoading(true);
    try {
      const data = await reelsApi.list(activeBrand.id);
      // Enrich with thumbnail_url and duration
      const enriched = await Promise.all(data.map(async (p) => {
        if (!p.thumbnail_url && p.result_video_id) {
          try {
            const v = await driveApi.getFile(p.result_video_id);
            return { ...p, thumbnail_url: v.thumbnail_url, duration_seconds: v.duration_seconds };
          } catch { /* ignore */ }
        }
        return p;
      }));
      setProjects(enriched);
    } catch {
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  }, [activeBrand]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este reel?')) return;
    try {
      await reelsApi.delete(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      toast.success('Reel eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  // ── Open editor ──────────────────────────────────────────────────────────────
  if (openProject !== null) {
    const isNew = openProject === 'new';
    // Build video object from result_url if existing project
    const video = !isNew && openProject.result_url
      ? {
        id: openProject.result_video_id || openProject.id,
        title: openProject.name,
        name: openProject.name,
        url: openProject.result_url,
        durationSeconds: openProject.duration_seconds || 30,
      }
      : null;

    return (
      <VideoEditorPage
        video={video}
        moduleType="reels"
        reelProject={isNew ? null : openProject}
        onSave={(updatedVideo) => {
          loadProjects();
          setOpenProject(null);
        }}
        onClose={() => { loadProjects(); setOpenProject(null); }}
        onExport={(v) => { toast.success('Exportando reel...'); }}
      />
    );
  }

  // ── Main projects grid ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen p-6" style={{ background: 'hsl(var(--background))' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black tracking-tight" style={{ color: 'hsl(var(--foreground))' }}>
            Reels Creator
          </h1>
          {activeBrand && (
            <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--primary))' }}>{activeBrand.name}</p>
          )}
        </div>
        <Button data-testid="new-reel-btn" onClick={() => setOpenProject('new')}
          className="gap-2 font-bold"
          style={{ background: 'hsl(var(--primary))', color: '#000', boxShadow: '0 0 20px hsl(var(--primary) / 0.3)' }}>
          <Plus size={16} /> Nuevo Reel
        </Button>
      </div>

      {/* Empty state */}
      {!loading && projects.length === 0 && (
        <div className="flex flex-col items-center justify-center py-32 text-center">
          <div className="w-20 h-20 rounded-3xl flex items-center justify-center mb-6"
            style={{ background: 'hsl(var(--surface))', border: '1px solid hsl(var(--border))' }}>
            <Film size={36} style={{ color: 'hsl(var(--foreground-subtle))' }} />
          </div>
          <h3 className="text-lg font-bold mb-2" style={{ color: 'hsl(var(--foreground))' }}>
            Sin reels todavía
          </h3>
          <p className="text-sm mb-8 max-w-xs" style={{ color: 'hsl(var(--foreground-muted))' }}>
            Crea tu primer reel. Añade clips, la IA genera los cortes, hooks y subtítulos automáticamente.
          </p>
          <Button data-testid="create-first-reel-btn" onClick={() => setOpenProject('new')}
            className="gap-2" style={{ background: 'hsl(var(--primary))', color: '#000' }}>
            <Plus size={16} /> Crear primer reel
          </Button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <RefreshCw size={24} className="animate-spin" style={{ color: 'hsl(var(--primary))' }} />
        </div>
      )}

      {/* Projects grid */}
      {!loading && projects.length > 0 && (
        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
          {/* New reel tile */}
          <button data-testid="new-reel-tile" onClick={() => setOpenProject('new')}
            className="rounded-2xl flex flex-col items-center justify-center gap-2 transition-colors hover:bg-surface-elevated"
            style={{ aspectRatio: '9/16', background: 'hsl(var(--surface))', border: '2px dashed hsl(var(--border))' }}>
            <Plus size={28} style={{ color: 'hsl(var(--primary))' }} />
            <span className="text-xs font-semibold" style={{ color: 'hsl(var(--primary))' }}>Nuevo Reel</span>
          </button>

          {projects.map(p => (
            <ProjectCard key={p.id} project={p} onOpen={setOpenProject} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
